export default (() => {
  if (!customElements.get('product-form')) {
    customElements.define('product-form', class ProductForm extends HTMLElement {
      constructor() {
        super();
      }
      initStickyElement() {
        const stickyElement = this.querySelector('[data-sticky]') || this.form?.hasAttribute('id') && document.querySelector(`[data-sticky="${this.form?.getAttribute('id')}"]`);
        if (stickyElement) {
          this.sticky = stickyElement;
          this.setupStickyBehavior();
        } else {
          const observer = new MutationObserver((mutationsList, observer) => {
            for (const mutation of mutationsList) {
              if (mutation.type === 'childList') {
                const addedNode = Array.from(mutation.addedNodes).find(node => node.nodeType === 1 && node.matches(`[data-sticky="${this.form?.getAttribute('id')}"]`));
                if (addedNode) {
                  this.sticky = addedNode;
                  this.setupStickyBehavior();
                  if (this.form?.getAttribute('id')) {
                    addedNode.querySelectorAll(`[form="${this.form.getAttribute('id')}"]`).forEach(el => el.addEventListener('change', this.onChange.bind(this)));
                  }
                  observer.disconnect();
                  return;
                }
              }
            }
          });
          observer.observe(document.body, {
            childList: true,
            subtree: true
          });
        }
      }
      getParent() {
        return this.closest('[data-product]') || this.closest('.shopify-section');
      }
      getTarget(event = false) {
        const id = event?.target?.getAttribute('form') || this.form?.getAttribute('id');
        if (id) {
          return [document, `[form="${id}"]`];
        } else {
          return [this, ''];
        }
      }
      buttons() {
        const [object, prefix] = this.getTarget();
        return object.querySelectorAll(`${prefix}[type="submit"]`);
      }
      onChange(event) {
        this.updateOptions(event);
        this.updateMasterId(event);
        this.updatePrice();
        this.updateInventory();
        this.updateURL();
        this.updateVariantImages();
        if (this.currentVariant) {
          if (this.currentVariant.available) {
            this.updateButton('available');
          } else {
            this.updateButton('soldOut');
          }
        } else {
          this.updateButton('unavailable');
        }
        this.dispatchEvent(new CustomEvent('variant:change', {
          bubbles: true,
          detail: {
            variant: this.currentVariant
          }
        }));

        // Close selection modal
        const modalTrigger = event.target.closest('[data-select-modal-wrapper]')?.querySelector('[data-select-modal-trigger]');
        if (modalTrigger) modalTrigger.checked = false;
      }
      updateOptions(event) {
        const [object, prefix] = this.getTarget(event);
        object.querySelectorAll(`${prefix}[data-option-title="${event.target.name}"]`).forEach(el => el.textContent = event.target.value);
        object.querySelectorAll(`${prefix}[name="${event.target.name}"]`).forEach(el => {
          if (el.tagName === 'SELECT') {
            el.value = event.target.value;
          } else {
            el.checked = el.value == event.target.value;
          }
        });
        const optionElements = Array.from(this.querySelectorAll('input[name^="option"], select[name^="option"]'));
        const optionNames = [...new Set(optionElements.map(el => el.name))].sort((a, b) => {
          return parseInt(a.replace('option', '')) - parseInt(b.replace('option', ''));
        });
        this.options = optionNames.map(name => {
          const element = this.querySelector(`select[name="${name}"], input[name="${name}"]:checked`);
          return element ? element.value : null;
        });
        if (this.dataset.updateAvailability === 'true') {
          this.updateAvailability(event);
        }
      }
      updateAvailability(event) {
        if (!this.options) return;
        const allOptions = this.querySelectorAll('input[name^="option"], select[name^="option"]');
        allOptions.forEach(optionInput => {
          const optionName = optionInput.name;
          const optionValue = optionInput.value;
          const optionIndex = parseInt(optionName.replace('option', '')) - 1;

          // Create a test combination: use current selections but swap the value for the current option being checked
          const testOptions = [...this.options];
          testOptions[optionIndex] = optionValue;

          // Find if there's an available variant with this combination
          const variants = this.getVariantData();
          const isAvailable = variants.some(variant => {
            if (!variant.available) return false;
            return !variant.options.map((opt, idx) => {
              const selection = testOptions[idx];
              // Wildcard match if no selection for this option
              if (selection === null || selection === undefined) return true;
              // Compare trimmed strings loosely
              return selection.toString().trim() == opt.toString().trim();
            }).includes(false);
          });

          // Toggle indicators
          const label = optionInput.closest('label');
          if (label) {
            const indicators = label.querySelectorAll('[data-oos-indicator]');
            indicators.forEach(indicator => {
              if (isAvailable) {
                indicator.classList.add('hidden');
              } else {
                indicator.classList.remove('hidden');
              }
            });
          }

          // Toggle aria attribute on input/label for accessibility/CSS hooks
          if (isAvailable) {
            optionInput.removeAttribute('aria-out-of-stock');
            optionInput.disabled = false;
            if (label) {
              label.removeAttribute('data-out-of-stock');
              label.setAttribute('data-close', '');
              label.removeAttribute('for');
              label.removeAttribute('data-event');
              label.removeAttribute('data-click-action');
              label.removeAttribute('data-product-name');
              label.removeAttribute('aria-label');
              label.removeAttribute('data-value');
            }
          } else {
            optionInput.setAttribute('aria-out-of-stock', 'true');
            optionInput.disabled = true;
            if (label) {
              label.setAttribute('data-out-of-stock', 'true');
              label.removeAttribute('data-close');
              label.setAttribute('for', `StockModal-${this.dataset.productId}`);
              label.setAttribute('data-event', 'notify_me_open');
              label.setAttribute('data-click-action', 'open');
              label.setAttribute('data-product-name', this.dataset.productTitle);
              label.setAttribute('aria-label', this.dataset.oosAriaLabel);
              label.setAttribute('data-value', optionValue);
            }
          }
        });
      }
      updateMasterId(event) {
        if (this.getVariantData().length === 1) {
          this.currentVariant = this.getVariantData()[0];
        } else {
          this.currentVariant = this.getVariantData().find(variant => {
            return !variant.options.map((option, index) => {
              return this.options[index] === option;
            }).includes(false);
          });
        }
        this.querySelector('[name="id"]').value = this.currentVariant ? this.currentVariant.id : '';
        if (this.currentVariant?.title) {
          const [object, prefix] = this.getTarget(event);
          object.querySelectorAll(`${prefix}[data-variant-title]`).forEach(el => el.dataset.variantTitle = this.currentVariant.title);
        }
      }
      updateInventory() {
        const inventory = this.querySelector('[data-inventory]');
        if (!inventory) return;
        if (this.lowInventory && this.currentVariant && this.currentVariant.inventory_quantity > 0 && this.currentVariant.inventory_quantity <= this.lowInventory && this.currentVariant.inventory_management == 'shopify' && this.currentVariant.inventory_policy == 'deny') {
          inventory.innerText = window.strings.inventory.low.replace('{{ count }}', this.currentVariant.inventory_quantity);
          //inventory.classList.remove('opacity-0');
        } else {
          //inventory.classList.add('opacity-0');
          //setTimeout(() => inventory.innerText = '', 500);
          inventory.innerText = '';
        }
      }
      updatePrice() {
        var price = this.getProductData().price,
          compareAtPrice = this.getProductData().compare_at_price;
        if (this.currentVariant) {
          price = this.currentVariant.price, compareAtPrice = this.currentVariant.compare_at_price;
        }
        this.getParent().querySelectorAll('[data-price]').forEach(el => el.innerHTML = Shopify.formatMoney(price, Shopify.money_format));
        compareAtPrice > price ? this.getParent().querySelectorAll('[data-compare-at-price]').forEach(el => el.innerHTML = Shopify.formatMoney(compareAtPrice, Shopify.money_format)) : this.getParent().querySelectorAll('[data-compare-at-price]').forEach(el => el.textContent = '');
      }
      updateVariantImages() {
        if (!this.currentVariant) return;
        const variantImages = this.getParent().querySelectorAll('[data-variants]');
        if (variantImages.length === 0) return;

        // Show images linked to current variant
        let index = 0;
        variantImages.forEach(el => {
          if (el.dataset.variants?.split(',').includes(this.currentVariant?.id.toString())) {
            el.classList.remove('hidden');
            index == 0 ? el.classList.add('col-span-2') : el.classList.remove('col-span-2');
          } else {
            el.classList.add('hidden');
            el.classList.remove('col-span-2');
          }
          index++;
        });

        // Show/hide images that are not variant specific
        this.getParent().querySelectorAll('[data-product-image]:not([data-variants])').forEach(el => {
          this.currentVariant.image ? el.classList.add('hidden') : el.classList.remove('hidden');
        });

        // Update the hidden input field with the current variant's featured image
        this.getParent().querySelectorAll('[name="properties[_image]"]').forEach(el => el.value = this.currentVariant.image);
      }
      updateButton(status) {
        var buttonText = this.dataset.selectText || window.strings.selectOptions,
          disabled = true;
        switch (status) {
          case 'available':
            buttonText = window.strings.addToCart;
            disabled = false;
            break;
          case 'soldOut':
            buttonText = window.strings.soldOut;
            break;
        }
        this.buttons().forEach(button => {
          button.disabled = disabled;
          button.textContent = buttonText;
          if (disabled) {
            button.classList.add('button-outline');
            button.classList.remove('button-primary');
          } else {
            button.classList.add('button-primary');
            button.classList.remove('button-outline');
          }
        });
      }
      updateURL() {
        if (this.getVariantData().length <= 1 || !this.currentVariant || this.hasAttribute('data-modal') || !this.querySelector('[type="submit"]')) return;
        const searchParams = new URLSearchParams(location.search);
        searchParams.set('variant', this.currentVariant.id);
        window.history.replaceState({}, '', `${this.dataset.url}?${searchParams.toString()}`);
      }
      getProductData() {
        if (this.querySelector('[type="application/json"][data-product-json]')) {
          this.productData = this.productData || JSON.parse(this.querySelector('[type="application/json"][data-product-json]').textContent);
        } else {
          this.productData = this.productData || JSON.parse(this.querySelector('[type="application/json"]').textContent);
        }
        return this.productData;
      }
      getVariantData() {
        if (this.querySelector('[type="application/json"][data-variant-json]')) {
          this.variantData = this.variantData && this.variantData[0].inventory_policy ? this.variantData : JSON.parse(this.querySelector('[type="application/json"][data-variant-json]').textContent);
        } else {
          this.variantData = this.variantData || this.getProductData().variants;
        }
        return this.variantData;
      }
      onSubmitHandler(event) {
        event.stopPropagation();
        event.stopImmediatePropagation();
        event.preventDefault();
        Shopify.addToCart(this.form);
      }

      // SET INITIAL SELECT OR RADIO INPUTS
      setupFirstInputElement() {
        const firstInputEl = this.querySelector('select[name^="option"],input[type="radio"]');
        if (firstInputEl) {
          if (firstInputEl.tagName === 'SELECT') {
            const firstValue = Array.from(firstInputEl.options).find(option => !option.disabled)?.value;
            if (firstValue) {
              firstInputEl.value = firstValue;
              const inputEvent = new Event('input', {
                bubbles: true,
                cancelable: true,
                composed: true
              });
              const changeEvent = new Event('change', {
                bubbles: true,
                cancelable: true
              });
              firstInputEl.dispatchEvent(inputEvent);
              firstInputEl.dispatchEvent(changeEvent);
            }
          } else if (this.getVariantData().length === 1) {
            firstInputEl.click();
          }
        }
      }
      setupOutofStockLabelClick() {
        const outOfStockLabelElements = this.querySelectorAll('label[data-out-of-stock]');
        outOfStockLabelElements.forEach(labelEl => {
          labelEl.addEventListener('click', event => {
            const clickedLabel = event.target.closest('label');
            if (!clickedLabel) return; // Guard against clicking on something that isn't a label

            const targetInputId = clickedLabel.getAttribute('for');
            if (!targetInputId) return; // Guard against labels without a 'for' attribute

            const modalInputEl = document.getElementById(targetInputId);
            if (!modalInputEl) return; // Guard against missing input element

            const modal = modalInputEl.nextElementSibling;
            if (!modal) return; // Guard against missing modal element

            const selectEl = modal.querySelector('select[name^="option"]');
            if (!selectEl) return; // Guard against missing select element

            const dataValue = clickedLabel.getAttribute('data-value');
            if (!dataValue) return; // Guard against missing data-value

            selectEl.value = dataValue;

            // Dispatch input and change events
            const inputEvent = new Event('input', {
              bubbles: true,
              cancelable: true,
              composed: true
            });
            const changeEvent = new Event('change', {
              bubbles: true,
              cancelable: true
            });
            selectEl.dispatchEvent(inputEvent);
            selectEl.dispatchEvent(changeEvent);
          });
        });
      }

      /*
       * Sometimes the <form> element has not yet rendered when the component is connected
       * so we need to watch for when it arrives.
       */
      connectedCallback() {
        this.form = this.querySelector('form');
        if (this.form) this.init();else {
          const observer = new MutationObserver((mutationsList, observer) => {
            this.form = this.querySelector('form');
            if (this.form) {
              this.init();
              observer.disconnect();
            }
          });
          observer.observe(this, {
            childList: true,
            subtree: true
          });
        }
      }
      init() {
        this.form = this.form || this.querySelector('form');
        this.sticky = null;
        this.lowInventory = parseInt(this.dataset.lowInventory);
        this.currentVariant = false;
        if (this.form?.hasAttribute('id') && this.form?.getAttribute('id')) {
          document.querySelectorAll(`[form="${this.form?.getAttribute('id')}"]`).forEach(el => el.addEventListener('change', this.onChange.bind(this)));
        } else {
          this.addEventListener('change', this.onChange.bind(this));
        }
        this.initStickyElement();
        this.setupFirstInputElement();
        this.setupOutofStockLabelClick();
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        let quantities = this.querySelectorAll('[data-quantity]');
        if (this.sticky) quantities = [...quantities, ...this.sticky.querySelectorAll('[data-quantity]')];
        quantities.forEach(el => {
          el.addEventListener('click', event => {
            event.preventDefault();
            event.stopPropagation();
            let quantity = parseInt(el.parentNode.querySelector('[name="quantity"]').value);
            quantity = el.hasAttribute('data-minus') ? Math.max(1, quantity - 1) : quantity = quantity + 1;
            this.getParent().querySelectorAll('[name="quantity"]').forEach(el => el.value = quantity);
          });
          el.classList.remove('hidden');
        });
        this.classList.remove('opacity-50', 'cursor-not-allowed');
      }
      setupStickyBehavior() {
        if (!this.sticky) return;
        const atc = this.querySelector('[data-atc]');
        const helpButton = document.getElementById('HelpButtonBubble');
        if (atc) {
          const observer = new IntersectionObserver(entries => {
            const entry = entries[0];
            if (!entry.isIntersecting && entry.target.getBoundingClientRect().y < window.innerHeight) {
              // Show sticky bar
              this.sticky.classList.remove('translate-y-full', 'opacity-0', 'pointer-events-none');
              if (helpButton) {
                // Move help button up so it sits above the sticky bar
                const stickyHeight = this.sticky.offsetHeight;
                helpButton.style.transform = `translateY(-${stickyHeight}px)`;
              }
            } else {
              // Hide sticky bar
              this.sticky.classList.add('translate-y-full', 'opacity-0', 'pointer-events-none');
              if (helpButton) {
                // Reset help button to original position
                helpButton.style.transform = '';
              }
            }
          }, {
            rootMargin: '-100px 0px 0px 0px'
          });
          observer.observe(atc);
        }

        // Scrollbar progress
        this.sticky.querySelectorAll('[data-scrollbar]').forEach(el => {
          el.parentNode.addEventListener('scroll', () => {
            let progress = Math.min(Math.max(0, el.parentNode.scrollTop / (el.parentNode.scrollHeight - el.parentNode.offsetHeight) * 100), 100);
            progress = progress * ((el.parentNode.children.length - 6) / (el.parentNode.children.length - 5));
            el.style.setProperty('--top', `${progress}%`);
          }, {
            passive: true
          });
        });
      }
    });
  }
})();